import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  ScrollView,
} from '../utils/webComponents';
import { useNavigation } from '../utils/navigation';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';
import { useFlashcards } from '../context/FlashcardContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import {
  ArrowBack,
  Check,
  Star,
  AutoAwesome,
  Analytics,
  CloudSync,
  OfflineBolt,
  Speed,
  Palette,
} from '@mui/icons-material';
import iapService from '../services/iapService';
import stripeService from '../services/stripeService';

const PricingScreen = () => {
  const navigation = useNavigation();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { isPro, syncUserDataFromFirestore, user } = useFlashcards();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [stripeInitialized, setStripeInitialized] = useState(false);

  useEffect(() => {
    const initializeServices = async () => {
      try {
        // Initialize Stripe
        const stripeReady = await stripeService.initialize();
        setStripeInitialized(stripeReady);

        // Load products (for display purposes)
        await iapService.initialize();
        const loadedProducts = iapService.getProducts();
        setProducts(loadedProducts);
      } catch (error) {
        console.error('Error initializing services:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeServices();
  }, []);

  // Check for successful checkout return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (urlParams.get('success') === 'true' && sessionId) {
      // Subscription successful - try to manually process the checkout session
      // This is a fallback if the webhook hasn't processed yet
      const processCheckoutSession = async () => {
        try {
          console.log('🔄 Processing checkout session:', sessionId);
          
          // Call a Cloud Function to manually process the checkout session
          const functions = getFunctions(getApp(), 'us-central1');
          const processCheckout = httpsCallable(functions, 'processCheckoutSession');
          
          const result = await processCheckout({ sessionId });
          console.log('✅ Checkout session processed:', result.data);
          
          // Wait a bit for Firestore to update
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Sync user data with retry
          const syncWithRetry = async (retries = 10, delay = 1000) => {
            for (let i = 0; i < retries; i++) {
              try {
                await syncUserDataFromFirestore();
                console.log(`✅ Subscription status synced (attempt ${i + 1}/${retries})`);
                
                // Check if we're now Pro
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // Reload page to show updated status
                window.location.reload();
                return;
              } catch (syncError) {
                console.error(`❌ Sync attempt ${i + 1} failed:`, syncError);
                if (i < retries - 1) {
                  await new Promise(resolve => setTimeout(resolve, delay));
                }
              }
            }
            // If all retries failed, still reload to show current state
            console.warn('⚠️ All sync retries failed, reloading anyway');
            window.location.reload();
          };
          
          await syncWithRetry();
        } catch (error) {
          console.error('❌ Error processing checkout session:', error);
          alert(`Error processing subscription: ${error.message}\n\nPlease try refreshing the page or use the "Refresh Subscription Status" button in your profile.`);
          
          // Still try to sync
          const syncWithRetry = async (retries = 5, delay = 2000) => {
            for (let i = 0; i < retries; i++) {
              try {
                await syncUserDataFromFirestore();
                console.log(`✅ Subscription status synced (attempt ${i + 1}/${retries})`);
                await new Promise(resolve => setTimeout(resolve, 500));
                if (i < retries - 1) {
                  await new Promise(resolve => setTimeout(resolve, delay));
                }
              } catch (syncError) {
                console.error(`❌ Sync attempt ${i + 1} failed:`, syncError);
                if (i < retries - 1) {
                  await new Promise(resolve => setTimeout(resolve, delay));
                }
              }
            }
          };
          syncWithRetry();
        }
      };
      
      processCheckoutSession();
      
      // Clear URL params after processing
      setTimeout(() => {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('success');
        newUrl.searchParams.delete('session_id');
        window.history.replaceState({}, document.title, newUrl.toString());
      }, 5000);
    }
  }, [syncUserDataFromFirestore]);

  const getProductPrice = (productId) => {
    const product = products.find(p => p.productId === productId);
    return product?.price || '';
  };

  const handlePurchase = async (productId) => {
    try {
      setPurchasing(true);

      // Map product IDs to Stripe Price IDs
      // Using production Price IDs directly (updated 2025-11-26)
      const priceIdMap = {
        'MONTHLY3000': 'price_1SXoBu9OIAwGg2rPBdhAG2VZ',
        'Yearly3000': 'price_1SXoL79OIAwGg2rPR8QFMVvm',
      };

      const priceId = priceIdMap[productId];

      console.log('💰 Product ID:', productId);
      console.log('💰 Using Price ID:', priceId);
      console.log('💰 All available Price IDs:', priceIdMap);

      if (!priceId) {
        throw new Error('Stripe Price ID not configured. Please set VITE_STRIPE_MONTHLY_PRICE_ID and VITE_STRIPE_YEARLY_PRICE_ID in your .env file.');
      }

      if (!stripeInitialized) {
        throw new Error('Stripe not initialized');
      }

      // Create checkout session and redirect
      await stripeService.checkout(priceId, 'subscription');
    } catch (error) {
      console.error('Purchase error:', error);
      alert(error.message || t('pricing.purchaseFailed'));
      setPurchasing(false);
    }
  };

  const isCurrentPlan = (productId) => {
    // Check if user has this specific subscription type
    const currentSubscriptionType = user?.subscriptionType;
    if (productId === 'MONTHLY3000') {
      return currentSubscriptionType === 'MONTHLY3000';
    }
    if (productId === 'Yearly3000') {
      return currentSubscriptionType === 'Yearly3000';
    }
    // For free plan, check if user is not Pro
    if (productId === 'FREE') {
      return !isPro();
    }
    return false;
  };

  const freeFeatures = [
    { icon: 'check', text: t('pricing.unlimitedClasses') },
    { icon: 'check', text: t('pricing.fiveSetsPerClass') },
    { icon: 'check', text: t('pricing.oneFiftyAIFlashcardsTotal') },
    { icon: 'check', text: t('pricing.smartSpacedRepetition') },
  ];

  const proFeatures = [
    { icon: 'check', text: t('pricing.unlimitedClasses') },
    { icon: 'check', text: t('pricing.unlimitedSets') },
    { icon: 'check', text: t('pricing.unlimitedAIFlashcards') },
    { icon: 'check', text: t('pricing.smartSpacedRepetition') },
  ];
  
  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <ArrowBack sx={{ fontSize: 24, color: theme.text }} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: theme.text }]}>
          {t('pricing.pricing')}
        </Text>
        <View style={{ width: 40 }} /> {/* Spacer for centering */}
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Hero Section */}
        <View style={styles.heroSection}>
          <Text style={[styles.heroTitle, { color: theme.text }]}>
            {t('pricing.studySmarterWithAI')}
          </Text>
          <Text style={[styles.heroSubtitle, { color: theme.textSecondary }]}>
            {t('pricing.pickYourPlanAndStartLearning')}
          </Text>
        </View>

        {/* Pricing Cards */}
        <View style={styles.pricingContainer}>
          {/* Free Plan */}
          <View style={[
            styles.pricingCard,
            { backgroundColor: theme.surface, borderColor: isCurrentPlan('FREE') ? (theme.success || '#50E3C2') : theme.border },
            isCurrentPlan('FREE') && styles.currentPlanCard,
          ]}>
            <View style={styles.cardHeader}>
              <Text style={[styles.planName, { color: theme.text }]}>
                {t('pricing.freePlan')}
              </Text>
              {isCurrentPlan('FREE') && (
                <View style={[styles.currentBadge, { backgroundColor: theme.success || '#50E3C2' }]}>
                  <Text style={[styles.currentBadgeText, { color: 'white' }]}>
                    {t('pricing.currentPlan')}
                  </Text>
                </View>
              )}
            </View>
            <View style={styles.priceSection}>
              <Text style={[styles.price, { color: theme.text }]}>$0</Text>
              <Text style={[styles.pricePeriod, { color: theme.textSecondary }]}>
                {t('pricing.perMonth')}
              </Text>
            </View>
            <View style={styles.featuresList}>
              {freeFeatures.map((feature, index) => (
                <View key={index} style={styles.featureItem}>
                  <Check sx={{ fontSize: 20, color: theme.success || '#50E3C2' }} />
                  <Text style={[styles.featureText, { color: theme.text }]}>
                    {feature.text}
                  </Text>
                </View>
              ))}
            </View>
          </View>

          {/* Pro Monthly */}
          <View style={[
            styles.pricingCard,
            styles.proCard,
            { backgroundColor: theme.surface, borderColor: isCurrentPlan('MONTHLY3000') ? (theme.success || '#50E3C2') : theme.primary },
            isCurrentPlan('MONTHLY3000') && styles.currentPlanCard,
          ]}>
            <View style={styles.cardHeader}>
              <Text style={[styles.planName, { color: theme.text }]}>
                {t('pricing.proMonthly')}
              </Text>
              {isCurrentPlan('MONTHLY3000') && (
                <View style={[styles.currentBadge, { backgroundColor: theme.success || '#50E3C2' }]}>
                  <Text style={[styles.currentBadgeText, { color: 'white' }]}>
                    {t('pricing.currentPlan')}
                  </Text>
                </View>
              )}
            </View>
            <View style={styles.priceSection}>
              <Text style={[styles.price, { color: theme.text }]}>
                {loading ? '...' : getProductPrice('MONTHLY3000')}
              </Text>
              <Text style={[styles.pricePeriod, { color: theme.textSecondary }]}>
                {t('pricing.perMonth')}
              </Text>
            </View>
            <View style={styles.featuresList}>
              {proFeatures.map((feature, index) => (
                <View key={index} style={styles.featureItem}>
                  <Check sx={{ fontSize: 20, color: theme.success || '#50E3C2' }} />
                  <Text style={[styles.featureText, { color: theme.text }]}>
                    {feature.text}
                  </Text>
                </View>
              ))}
            </View>
            <TouchableOpacity
              style={[
                styles.subscribeButton,
                { backgroundColor: isCurrentPlan('MONTHLY3000') ? theme.border : theme.primary },
              ]}
              onPress={() => handlePurchase('MONTHLY3000')}
              disabled={purchasing || isCurrentPlan('MONTHLY3000')}
            >
              <Text style={[styles.subscribeButtonText, { color: 'white' }]}>
                {purchasing ? t('pricing.processing') : isCurrentPlan('MONTHLY3000') ? t('pricing.currentPlan') : t('pricing.goProMonthly')}
              </Text>
            </TouchableOpacity>
          </View>

          {/* Pro Yearly */}
          <View style={[
            styles.pricingCard,
            styles.proCard,
            styles.bestValueCard,
            { backgroundColor: theme.surface, borderColor: isCurrentPlan('Yearly3000') ? (theme.success || '#50E3C2') : theme.primary },
            isCurrentPlan('Yearly3000') && styles.currentPlanCard,
          ]}>
            <View style={styles.bestValueBadge}>
              <Star sx={{ fontSize: 18, color: 'white' }} />
              <Text style={styles.bestValueText}>{t('pricing.bestValue')}</Text>
            </View>
            <View style={styles.cardHeader}>
              <Text style={[styles.planName, { color: theme.text }]}>
                {t('pricing.proYearly')}
              </Text>
              {isCurrentPlan('Yearly3000') && (
                <View style={[styles.currentBadge, { backgroundColor: theme.success || '#50E3C2' }]}>
                  <Text style={[styles.currentBadgeText, { color: 'white' }]}>
                    {t('pricing.currentPlan')}
                  </Text>
                </View>
              )}
            </View>
            <View style={styles.priceSection}>
              <Text style={[styles.price, { color: theme.text }]}>
                {loading ? '...' : getProductPrice('Yearly3000')}
              </Text>
              <Text style={[styles.pricePeriod, { color: theme.textSecondary }]}>
                {t('pricing.perYear')}
              </Text>
            </View>
            <View style={styles.savingsBadge}>
              <Text style={[styles.savingsText, { color: theme.success || '#50E3C2' }]}>
                {t('pricing.save35PercentWithYearlyBilling')}
              </Text>
            </View>
            <View style={styles.featuresList}>
              {proFeatures.map((feature, index) => (
                <View key={index} style={styles.featureItem}>
                  <Check sx={{ fontSize: 20, color: theme.success || '#50E3C2' }} />
                  <Text style={[styles.featureText, { color: theme.text }]}>
                    {feature.text}
                  </Text>
                </View>
              ))}
            </View>
            <TouchableOpacity
              style={[
                styles.subscribeButton,
                { backgroundColor: isCurrentPlan('Yearly3000') ? theme.border : theme.primary },
              ]}
              onPress={() => handlePurchase('Yearly3000')}
              disabled={purchasing || isCurrentPlan('Yearly3000')}
            >
              <Text style={[styles.subscribeButtonText, { color: 'white' }]}>
                {purchasing ? t('pricing.processing') : isCurrentPlan('Yearly3000') ? t('pricing.currentPlan') : (isCurrentPlan('MONTHLY3000') ? t('pricing.upgradeToYearly') : t('pricing.goProYearly'))}
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* FAQ Section */}
        <View style={styles.faqSection}>
          <View style={[styles.faqItem, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            <Text style={[styles.faqQuestion, { color: theme.text }]}>
              {t('pricing.canICancelAnytime')}
            </Text>
            <Text style={[styles.faqAnswer, { color: theme.textSecondary }]}>
              {t('pricing.yesInstantlyFromSettings')}
            </Text>
          </View>

          <View style={[styles.faqItem, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            <Text style={[styles.faqQuestion, { color: theme.text }]}>
              {t('pricing.whatsTheFreePlanLimit')}
            </Text>
            <Text style={[styles.faqAnswer, { color: theme.textSecondary }]}>
              {t('pricing.threeClassesAndOneFiftyAIFlashcards')}
            </Text>
          </View>
    </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 16,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    flex: 1,
    textAlign: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
    maxWidth: 1200,
    width: '100%',
    margin: '0 auto',
  },
  heroSection: {
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 32,
    display: 'flex',
    alignItems: 'center',
  },
  heroTitle: {
    fontSize: 28,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12,
  },
  heroSubtitle: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 1.5,
  },
  pricingContainer: {
    paddingHorizontal: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    marginBottom: 40,
  },
  pricingCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  currentPlanCard: {
    borderWidth: 3,
    borderStyle: 'solid',
  },
  proCard: {
    borderWidth: 2,
  },
  bestValueCard: {
    marginTop: 8,
  },
  bestValueBadge: {
    position: 'absolute',
    top: -18,
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#50E3C2',
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 24,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    zIndex: 10,
  },
  bestValueText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '700',
  },
  cardHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  planName: {
    fontSize: 24,
    fontWeight: '700',
  },
  currentBadge: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 18,
    marginLeft: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  currentBadgeText: {
    fontSize: 14,
    fontWeight: '700',
  },
  priceSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: 24,
    gap: 8,
  },
  price: {
    fontSize: 36,
    fontWeight: '700',
  },
  pricePeriod: {
    fontSize: 16,
  },
  savingsBadge: {
    marginBottom: 16,
  },
  savingsText: {
    fontSize: 14,
    fontWeight: '600',
  },
  featuresList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 24,
  },
  featureItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  featureText: {
    fontSize: 16,
    lineHeight: 1.5,
  },
  subscribeButton: {
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  subscribeButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  faqSection: {
    paddingHorizontal: 24,
    marginBottom: 40,
  },
  faqItem: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 20,
    marginBottom: 16,
  },
  faqQuestion: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
  },
  faqAnswer: {
    fontSize: 16,
    lineHeight: 1.5,
  },
});

export default PricingScreen;
